import React from 'react';
import {
  Box,
  Card,
  CardActionArea,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HubIcon from '@mui/icons-material/Hub';
import StarIcon from '@mui/icons-material/Star';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CodeIcon from '@mui/icons-material/Code';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import { STUDIO_FEATURE_HIGHLIGHTS } from '../../pages/studio/studioFeatureHighlights';

const ICONS = {
  hub: HubIcon,
  star: StarIcon,
  extension: ExtensionOutlinedIcon,
  help: HelpOutlineIcon,
  code: CodeIcon,
  work: WorkOutlineIcon,
};

export default function StudioFeatureHighlights({
  title = 'Platform features',
  subtitle = 'Extensions, MCP tools, in-app help, and recruiting playbooks — all wired into Studio V2.',
  items = STUDIO_FEATURE_HIGHLIGHTS,
  variant = 'studio',
}) {
  const isDashboard = variant === 'dashboard';

  return (
    <Box>
      {(title || subtitle) && (
        <Box sx={{ mb: isDashboard ? 2 : 2.5 }}>
          {title && (
            <Typography
              variant={isDashboard ? 'h6' : 'subtitle1'}
              fontWeight={700}
              sx={{ color: 'text.primary', mb: subtitle ? 0.5 : 0 }}
            >
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      )}
      <Grid container spacing={isDashboard ? 2 : 2}>
        {items.map((item) => {
          const Icon = ICONS[item.icon] || HelpOutlineIcon;
          const tone = item.tone || 'primary';
          return (
            <Grid item xs={12} sm={6} md={4} key={item.id}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  borderRadius: isDashboard ? 3 : 3,
                  border: 1,
                  borderColor: 'divider',
                  ...(isDashboard && {
                    background: (theme) => (theme.palette.mode === 'dark'
                      ? 'rgba(30, 30, 30, 0.85)'
                      : 'rgba(255, 255, 255, 0.88)'),
                    backdropFilter: 'blur(12px)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 32px rgba(25, 118, 210, 0.12)',
                    },
                  }),
                }}
              >
                <CardActionArea
                  component={RouterLink}
                  to={item.path}
                  sx={{ p: isDashboard ? 2 : 2.5, height: '100%', alignItems: 'stretch' }}
                >
                  <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ height: '100%' }}>
                    <Box
                      sx={(theme) => ({
                        p: 1.25,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette[tone]?.main || theme.palette.primary.main, 0.12),
                        color: `${tone}.main`,
                        display: 'flex',
                        flexShrink: 0,
                      })}
                    >
                      <Icon />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" fontWeight={700}>{item.title}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {item.description}
                      </Typography>
                    </Box>
                    <ChevronRightIcon color="action" sx={{ flexShrink: 0, mt: 0.25 }} />
                  </Stack>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
